import React from "react"

import {EntityType} from "../../../gd/const/item"
import ApiRelation from "../../../gd/api/graphql/relation"
import Relation from "../relation"
import RelationsCollection from "../collection"
import {relationEntityFromJson} from "../helpers/entity-factory"
import {MentionOption} from "../../../gd-components/editor/plugins/mentions/option"

// Maps a picked selector option onto the backend's compact summary shape, so the
// optimistic row is built exactly like a server-delivered one (the selector
// hands the real model through on `option.data`).
//
// Not in helpers/entity-factory on purpose: it must not drag the lexical
// MentionOption import into the factory.
function mentionOptionToSummary(option: MentionOption): any {
    const data = option.data || {}

    switch (option.type) {
        case EntityType.TASK:
            return {
                id          : option.id,
                title       : option.name,
                taskTypeId  : data.taskTypeId,
                taskStatusId: data.statusId,
                systemType  : data.systemType,
                systemStatus: data.systemStatus,
                projectId   : data.projectId,
                shortId     : data.shortId,
            }

        case EntityType.DOCUMENT:
            return {
                id          : option.id,
                name        : option.name,
                projectId   : data.projectId,
                noteTypeId  : data.noteTypeId,
                systemType  : data.systemType,
            }

        case EntityType.PROJECT:
            return {
                id          : option.id,
                name        : option.name,
                icon        : data.icon,
                color       : data.color,
                systemType  : data.systemType,
                systemStatus: data.systemStatus,
                parentId    : data.parentId,
            }

        default:
            return {id: option.id, name: option.name, title: option.name}
    }
}

// Creates a MANUAL_LINK from the current item to the chosen entity, then adds
// the row optimistically (the live relation event upgrades it in place).
//
// Nothing re-renders from here: adding fires the collection's own bus event
// (BUS_RELATIONS_CHANGED), which is what the hosts subscribe to.
export async function createRelatedItem(relations: RelationsCollection, entity: MentionOption) {

    const current = relations.currentItem

    // no self-link
    if (entity.type === current.itemType && entity.id === current.id) {
        gd.modals.other.notification(<div>You cannot link an item to itself.</div>)
        return
    }

    try {
        const result: any = await ApiRelation.create({
            sourceItemType : current.itemType,
            sourceItemId   : current.id,
            targetItemType : entity.type,
            targetItemId   : entity.id,
        }).promise()

        // The live event may already have landed under this id. A row we must
        // not show (e.g. a USER link) is dropped by the collection.
        if (relations.get(result.id)) return

        relations.addRelation(new Relation({
            id              : result.id,
            type            : result.type,
            metadata        : result.metadata,
            sourceItemType  : result.sourceItemType,
            sourceItemId    : result.sourceItemId,
            targetItemType  : result.targetItemType,
            targetItemId    : result.targetItemId,

            sourceItem      : current,      // own side: never rendered
            targetItem      : relationEntityFromJson(entity.type, mentionOptionToSummary(entity)),
        }, current))

    } catch (err: any) {
        gd.modals.other.notification(<div>{String(err?.message || 'Failed to add related item')}</div>)
    }
}
